/**
 * SCT AGAINST THE LINE — Jay via Max, 2026-10-18, from the DAC productivity board ("SMV library ·
 * automatic check"); Max: "làm theo đề xuất tốt nhất nhé". The server (sctActual.js) measures and
 * flags; this screen shows the list, the figures behind each flag, the answers, and what the AI made
 * of each answer.
 *
 * ONE ROW PER ITEM × PART × THE CT IT WAS MEASURED AGAINST. Actual = Σ(workers × hours) ÷ Σ pieces
 * from Daily Output over the period; efficiency = CT ÷ actual. Out of the band the row needs an
 * answer. The AI reads the answer: "explained" closes it, "not explained" leaves it waiting for more.
 * A person may still close it themselves — the AI advises, it does not decide — and open it again.
 *
 * The words are in src/locales/xSctActual.js, in the portal's four languages (2026-10-21).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, RefreshCw, Check, RotateCcw, Send, Pencil, CalendarClock, X, Plus, Info } from 'lucide-react';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import useAuth from './useAuth';
import { fmtDateTimeVN } from './timeZone';
import AiTag from './AiTag';
import './ProjectApproval.css';
import './SctActual.css';

const n2 = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2));
const n0 = (v) => (v === null || v === undefined ? '—' : Math.round(Number(v)).toLocaleString('en-US'));
const pct = (v) => `${Math.round(Number(v) * 100)}%`;
const ddmm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const vnToday = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
const daysBack = (n) => new Date(Date.now() + 7 * 3600e3 - n * 86400e3).toISOString().slice(0, 10);

/* The labels are read at render (presetLabel in the component), so they follow the language. */
const PRESETS = [
  { key: 'year', from: () => `${vnToday().slice(0, 4)}-01-01` },
  { key: '90', from: () => daysBack(90) },
  { key: '30', from: () => daysBack(30) },
];

/* What a row is waiting on, in words — the chip is never colour alone. The words are the component's
   statusLabel; `cls` stays here. */
const STATUS = {
  reply: { cls: 'reply' },
  more: { cls: 'reply' },
  handled: { cls: 'handled' },
  ok: { cls: 'ok' },
  thin: { cls: 'thin' },
};
const statusOf = (r) => (r.status === 'reply' && r.needsMore ? 'more' : r.status);
const basisOf = (t, r) => `${r.ctSource === 'sct' ? t('xSctActual.basisSct') : t('xSctActual.basisWorkbook')} ${r.ctSeason || ''}`.trim();
const itemName = (r) => (r.sapCode.startsWith('~') ? r.item : r.item || r.sapCode);

/* The automatic check, worded by CODE from the row's own figures — the same facts the AI is given,
   so the sentence and the judgement cannot disagree about what was measured. */
const checkSentence = (t, r, band) => {
  const e = pct(r.eff);
  if (r.direction === 'over') {
    return t('xSctActual.checkOver', { ct: n2(r.ct), actual: n2(r.actual), eff: e, max: pct(band.max) });
  }
  return t('xSctActual.checkUnder', { actual: n2(r.actual), ct: n2(r.ct), eff: e, min: pct(band.min) });
};

/* A translated sentence with elements inside it: the placeholders named in `parts` are filled with a
   marker, the string is split on the markers and the elements dropped in — so each language keeps its
   own word order around the bold range and the AI mark. */
// The markers are control characters on purpose: they cannot occur in any translation.
// eslint-disable-next-line no-control-regex
const fillParts = (text, parts) => text.split(/(\u0001\w+\u0002)/).map((piece, i) => {
  // eslint-disable-next-line no-control-regex
  const m = piece.match(/^\u0001(\w+)\u0002$/);
  // eslint-disable-next-line react/no-array-index-key
  return m ? <React.Fragment key={i}>{parts[m[1]]}</React.Fragment> : piece;
});
const marker = (name) => `\u0001${name}\u0002`;

/* WHO ANSWERS, AND BY WHEN — DAC's "Hẹn xong trước" (Max, 2026-10-25: "bổ sung thêm 2 phần"). Each person who
   answers the flags may pick the day they will have answered by; until then the 08:00 reminder in the bell leaves
   them alone, after it the reminder comes back. The list is kept here — nobody else in the portal knows who owns a
   cycle time. */
function SaPeople({ t, base, canEdit }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState('');
  const call = async (method, path, body) => {
    setErr('');
    const r = await fetch(`${base}/people${path}`, { method, credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(j.error || `HTTP ${r.status}`); return null; }
    return j;
  };
  const load = useCallback(async () => { const j = await call('GET', ''); if (j) setD(j); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  /* a day is saved the moment it is picked (or cleared) — one box per person, no Save button per row */
  const setDay = async (p, date) => { const j = await call('PUT', `/${p.userId}/promise`, { date }); if (j) setD((x) => ({ ...x, people: j.people })); };
  const dayText = (p) => (p.promise ? (p.overdue ? t('xSctActual.promisePassed', { date: ddmm(p.promise) }) : t('xSctActual.promiseBy', { date: ddmm(p.promise) }))
    : t('xSctActual.promiseNone'));

  /* SHUT: one line naming everybody and their day. OPEN: a small table, a row per person. Max, 25/10: "UI này tôi thấy
     chưa okie lắm" — five pills each carrying a date box, Save and × wrapped onto two rows above the list. */
  return (
    <div className={`sa-people${editing ? ' open' : ''}`}>
      <div className="sa-people-head">
        <CalendarClock size={15} />
        <b>{t('xSctActual.promiseTitle')}</b>
        <span className="sa-people-info" title={t('xSctActual.promiseHint')} aria-label={t('xSctActual.promiseHint')}><Info size={13} /></span>
        {!editing && (
          <span className="sa-people-sum">
            {!d.people.length && <span className="pa-muted">{t('xSctActual.promiseNobody')}</span>}
            {d.people.map((p, i) => (
              <span key={p.userId} className={`sa-people-one${p.overdue ? ' late' : p.promise ? ' set' : ''}`} data-uid={p.userId}>
                {i > 0 && <span className="sa-dot" aria-hidden="true">·</span>}
                {p.name} <em>{dayText(p)}</em>
              </span>
            ))}
          </span>
        )}
        <span className="sa-grow" />
        {canEdit && (
          <button type="button" className="pa-btn small" onClick={() => setEditing((v) => !v)}>
            {editing ? t('xSctActual.promiseDone') : <><Pencil size={13} /> {t('xSctActual.promiseEdit')}</>}
          </button>
        )}
      </div>
      {editing && (
        <table className="sa-people-table">
          <thead><tr><th>{t('xSctActual.promiseColPerson')}</th><th>{t('xSctActual.promiseColDay')}</th><th>{t('xSctActual.promiseColState')}</th><th aria-label={t('xSctActual.promiseRemove')} /></tr></thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.userId} data-uid={p.userId} className={p.overdue ? 'late' : ''}>
                <td><b>{p.name}</b></td>
                <td>
                  <input type="date" value={p.promise || ''} min={d.today} onChange={(e) => setDay(p, e.target.value)}
                    aria-label={t('xSctActual.promiseDate', { name: p.name })} />
                  {p.promise && <button type="button" className="sa-link" onClick={() => setDay(p, '')}>{t('xSctActual.promiseClear')}</button>}
                </td>
                <td className="sa-people-state">{dayText(p)}</td>
                <td><button type="button" className="sa-person-x" title={t('xSctActual.promiseRemove')} aria-label={t('xSctActual.promiseRemove')}
                  onClick={async () => { const j = await call('DELETE', `/${p.userId}`); if (j) load(); }}><X size={14} /></button></td>
              </tr>
            ))}
            <tr className="sa-people-addrow">
              <td colSpan={4}>
                <select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label={t('xSctActual.promiseAdd')}>
                  <option value="">{t('xSctActual.promiseAdd')}</option>
                  {d.candidates.map((c) => <option key={c.userId} value={c.userId}>{c.name}</option>)}
                </select>
                <button type="button" className="pa-btn small" disabled={!adding}
                  onClick={async () => { if (await call('POST', '', { userId: Number(adding) })) { setAdding(''); load(); } }}><Plus size={13} /> {t('xSctActual.promiseAddBtn')}</button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {err && <div className="pa-banner err">{err}</div>}
    </div>
  );
}

export default function SctActual({ apiBase }) {
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const presetLabel = { year: t('xSctActual.presetYear'), 90: t('xSctActual.preset90'), 30: t('xSctActual.preset30') };
  const statusLabel = {
    reply: t('xSctActual.stReply'), more: t('xSctActual.stMore'), handled: t('xSctActual.stHandled'),
    ok: t('xSctActual.stOk'), thin: t('xSctActual.stThin'),
  };
  const canAnswer = permissions?.opa_sct === 'edit' || permissions?.capacity === 'edit';
  const [preset, setPreset] = useState('year');
  const [from, setFrom] = useState(PRESETS[0].from());
  const [to, setTo] = useState(vnToday());
  const [vendor, setVendor] = useState('');
  const [chip, setChip] = useState('reply');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);       // the row key (identity) that is open
  const [detail, setDetail] = useState({});     // identity -> { days, notes } | 'loading'
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState(null);     // { id, text, ok }
  const [fix, setFix] = useState(null);         // { id, ct, reason } while correcting a CT

  const base = `${apiBase}/opa/sct-actual`;
  const params = useMemo(() => new URLSearchParams({ from, to, vendor }).toString(), [from, to, vendor]);
  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${base}?${params}`, { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j); setDetail({});
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [base, params]);
  useEffect(() => { load(); }, [load]);

  const idOf = (r) => `${r.sapCode}|${r.part}|${r.ctSeason}|${r.ct}`;
  const rows = useMemo(() => (data?.rows || []).map((r) => ({ ...r, id: idOf(r), st: statusOf(r) })), [data]);
  const counts = useMemo(() => {
    const c = { reply: 0, handled: 0, ok: 0, thin: 0, all: rows.length };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);
  const pre = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => (chip === 'all' || r.status === chip)
      && (!s || [r.item, r.colour, r.sap, r.part, r.vendors].some((v) => String(v || '').toLowerCase().includes(s))));
  }, [rows, chip, q]);

  const effCls = (r) => (r.eff > (data?.band?.max ?? 1) ? 'sa-eff over' : (r.eff < (data?.band?.min ?? 0.5) ? 'sa-eff under' : 'sa-eff'));
  const columns = [
    { key: 'item', label: t('xSctActual.colItem'), get: (r) => itemName(r),
      cell: (r) => (<><b>{itemName(r)}</b>{r.colour && <span className="pa-muted"> · {r.colour}</span>}</>) },
    { key: 'part', label: t('xSctActual.colPart'), get: (r) => r.part },
    { key: 'sap', label: t('xSctActual.colSap'), get: (r) => r.sap || '', td: 'mono' },
    { key: 'basis', label: t('xSctActual.colBasis'), get: (r) => basisOf(t, r) },
    { key: 'vendors', label: t('xSctActual.colVendor'), get: (r) => r.vendors },
    { key: 'days', label: t('xSctActual.colDays'), get: (r) => r.days, td: 'num' },
    { key: 'pieces', label: t('xSctActual.colPieces'), get: (r) => r.pieces, td: 'num', cell: (r) => n0(r.pieces) },
    { key: 'ct', label: t('xSctActual.colCt'), get: (r) => r.ct, td: 'num', cell: (r) => n2(r.ct) },
    { key: 'actual', label: t('xSctActual.colActual'), get: (r) => r.actual, td: 'num', cell: (r) => n2(r.actual) },
    { key: 'eff', label: t('xSctActual.colEff'), get: (r) => Math.round(r.eff * 100), td: 'num', cell: (r) => <span className={effCls(r)}>{pct(r.eff)}</span> },
    /* The AI tag rides beside the two states the AI decided: closed because it accepted an answer, and
       waiting because it did not. A state a person set, or the arithmetic set, carries none. */
    { key: 'st', label: t('xSctActual.colStatus'), get: (r) => statusLabel[r.st],
      cell: (r) => (
        <span className="sa-st">
          <span className={`sa-chip ${STATUS[r.st].cls}`}>{statusLabel[r.st]}</span>
          {((r.st === 'handled' && r.handledHow === 'ai') || r.st === 'more') && <AiTag title={t('xSctActual.aiDecided')} />}
        </span>
      ) },
  ];
  const tools = useColumnTools(pre, columns);

  const loadDetail = useCallback(async (r) => {
    setDetail((d) => ({ ...d, [r.id]: 'loading' }));
    const p = new URLSearchParams({ from, to, vendor, sapCode: r.sapCode, part: r.part, ctSeason: r.ctSeason, ct: r.ct });
    try {
      const x = await fetch(`${base}/detail?${p}`, { credentials: 'include' });
      const j = await x.json();
      if (!x.ok) throw new Error(j.error || `HTTP ${x.status}`);
      setDetail((d) => ({ ...d, [r.id]: j }));
    } catch (e) { setDetail((d) => ({ ...d, [r.id]: { error: e.message } })); }
  }, [base, from, to, vendor]);

  const toggle = (r) => {
    if (open === r.id) { setOpen(null); return; }
    setOpen(r.id); setDraft(''); setFlash(null); setFix(null);
    if (!detail[r.id] || detail[r.id].error) loadDetail(r);
  };

  const post = async (r, path, extra) => {
    setSending(true); setFlash(null);
    try {
      const x = await fetch(`${base}/${path}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to, vendor, sapCode: r.sapCode, part: r.part, ctSeason: r.ctSeason, ct: r.ct, ...extra }),
      });
      const j = await x.json();
      if (!x.ok) throw new Error(j.error || `HTTP ${x.status}`);
      return j;
    } catch (e) { setFlash({ id: r.id, ok: false, text: e.message }); return null; } finally { setSending(false); }
  };
  // After a write: the list's state and the row's notes both moved. The open row stays open.
  const refresh = async (r) => { await load(); loadDetail(r); };

  const answer = async (r) => {
    const j = await post(r, 'answer', { text: draft });
    if (!j) return;
    setDraft('');
    setFlash({ id: r.id, ok: j.state === 'handled',
      text: j.verdict ? (j.verdict.verdict === 'clear' ? t('xSctActual.aiAccepts')
        : t('xSctActual.aiNotEnough'))
        : t('xSctActual.savedNoAi', { err: j.aiError }) });
    refresh(r);
  };
  const mark = async (r) => { if (await post(r, 'mark', { note: draft })) { setDraft(''); refresh(r); } };
  const reopen = async (r) => { if (await post(r, 'reopen', {})) refresh(r); };
  /* CORRECT THE CT — DAC's "Sửa và tải lại SMV": the standard itself is wrong, so it is corrected here, on every order
     line of the item × part × season carrying that CT; the item is then measured afresh against the new figure. */
  const saveCt = async (r) => {
    const j = await post(r, 'ct', { newCt: Number(fix.ct), reason: fix.reason });
    if (!j) return;
    setFix(null);
    setFlash({ id: r.id, ok: true, text: `${t('xSctActual.ctSaved', { old: n2(j.oldCt), new: n2(j.newCt), lines: j.lines, eff: pct(j.effNow) })}${j.fromSct ? ` ${t('xSctActual.ctFromSct')}` : ''}` });
    await load();
  };

  const pickPreset = (k) => {
    setPreset(k);
    const p = PRESETS.find((x) => x.key === k);
    if (p) { setFrom(p.from()); setTo(vnToday()); }
  };

  const noteView = (n) => (
    <div key={n.id} className={`sa-note ${n.kind}`}>
      <div className="sa-note-head">
        <b>{n.kind === 'answer' ? t('xSctActual.noteAnswer') : n.kind === 'mark' ? t('xSctActual.noteMark') : n.kind === 'ct' ? t('xSctActual.noteCt') : t('xSctActual.noteReopen')}</b>
        <span className="pa-muted"> · {n.by} · {fmtDateTimeVN(n.at)}</span>
      </div>
      {n.body && <div className="sa-note-body">{n.body}</div>}
      {n.kind === 'answer' && (n.verdict ? (
        <div className={`sa-ai ${n.verdict === 'clear' ? 'clear' : 'notclear'}`}>
          <AiTag title={t('xSctActual.aiReading')} />
          {' '}<b>{n.verdict === 'clear' ? t('xSctActual.explained') : t('xSctActual.notExplained')}</b> {n.reason}
          {n.next && <div className="sa-ai-next">→ {n.next}</div>}
        </div>
      ) : <div className="sa-ai none"><AiTag /> {t('xSctActual.aiNotReviewed')}</div>)}
    </div>
  );

  const detailView = (r) => {
    const d = detail[r.id];
    const flagged = r.status === 'reply' || r.status === 'handled';
    return (
      <div className="sa-detail">
        {flagged && (
          <div className={`sa-check ${r.status === 'handled' ? 'done' : ''}`}>
            <div className="sa-check-head"><b>{t('xSctActual.autoCheck')}</b>
              <span className="pa-muted"> · {t('xSctActual.checkMeta', { from: ddmm(r.firstDay), to: ddmm(r.lastDay), days: r.days, pieces: n0(r.pieces), manHours: n0(r.manHours) })}</span>
            </div>
            <div className="sa-check-text">{checkSentence(t, r, data.band)}</div>
            {d && d !== 'loading' && !d.error && d.notes.filter((n) => n.direction === r.direction).map(noteView)}
            {r.status === 'handled' && (
              <div className="sa-handled-line">
                <Check size={14} /> {r.handledHow === 'ai' ? <>{t('xSctActual.handledByAi')} <AiTag /></> : t('xSctActual.handledByHand')}
                {r.handledBy ? ` · ${r.handledBy}` : ''}{r.handledAt ? ` · ${fmtDateTimeVN(r.handledAt)}` : ''}
              </div>
            )}
            {canAnswer && r.status === 'reply' && (
              <div className="sa-answer">
                <textarea rows={3} value={draft} maxLength={1000} onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('xSctActual.answerPlaceholder')} />
                <div className="sa-answer-acts">
                  <button type="button" className="pa-btn primary" disabled={sending || draft.trim().length < 10} onClick={() => answer(r)}
                    title={draft.trim().length < 10 ? t('xSctActual.min10') : t('xSctActual.sendTitle')}>
                    {sending ? <Loader2 size={14} className="pa-spin" /> : <Send size={14} />} {t('xSctActual.sendExplanation')}
                  </button>
                  <button type="button" className="pa-btn" disabled={sending} onClick={() => mark(r)}
                    title={t('xSctActual.markTitle')}>
                    <Check size={14} /> {t('xSctActual.markHandled')}
                  </button>
                  <button type="button" className="pa-btn" disabled={sending} aria-expanded={fix?.id === r.id}
                    onClick={() => setFix(fix?.id === r.id ? null : { id: r.id, ct: n2(r.ct), reason: '' })} title={t('xSctActual.ctTitle')}>
                    <Pencil size={14} /> {t('xSctActual.ctFix')}
                  </button>
                </div>
                {fix?.id === r.id && (
                  <div className="sa-ctfix">
                    <label>{t('xSctActual.ctNow')} <b>{n2(r.ct)}</b> h/pc · {t('xSctActual.ctActual')} <b>{n2(r.actual)}</b></label>
                    <label className="sa-ctfix-new">{t('xSctActual.ctNew')}
                      <input inputMode="decimal" value={fix.ct} onChange={(e) => setFix({ ...fix, ct: e.target.value.replace(/[^0-9.]/g, '') })} aria-label={t('xSctActual.ctNew')} />
                      h/pc
                      {Number(fix.ct) > 0 && <span className="pa-muted">→ {t('xSctActual.ctEffWould', { eff: pct(Number(fix.ct) / r.actual) })}</span>}
                    </label>
                    <textarea rows={2} value={fix.reason} maxLength={900} onChange={(e) => setFix({ ...fix, reason: e.target.value })}
                      placeholder={t('xSctActual.ctReason')} />
                    {r.ctSource === 'sct' && <div className="pa-muted">{t('xSctActual.ctSctNote')}</div>}
                    <div className="sa-answer-acts">
                      <button type="button" className="pa-btn primary" disabled={sending || !(Number(fix.ct) > 0) || fix.reason.trim().length < 10}
                        onClick={() => saveCt(r)}>{sending ? <Loader2 size={14} className="pa-spin" /> : <Check size={14} />} {t('xSctActual.ctSave')}</button>
                      <button type="button" className="pa-btn" onClick={() => setFix(null)}>{t('xSctActual.ctCancel')}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {canAnswer && r.status === 'handled' && (
              <div className="sa-answer-acts">
                <button type="button" className="pa-btn" disabled={sending} onClick={() => reopen(r)}><RotateCcw size={14} /> {t('xSctActual.reopen')}</button>
              </div>
            )}
            {flash && flash.id === r.id && <div className={`pa-banner ${flash.ok ? 'ok' : 'err'}`}>{flash.text}</div>}
          </div>
        )}
        {r.status === 'thin' && (
          <div className="sa-check thin">{t('xSctActual.thin', { count: r.days, min: data.band.minDays })}</div>
        )}
        {d === 'loading' && <div className="pa-muted"><Loader2 size={14} className="pa-spin" /> {t('xSctActual.loadingDays')}</div>}
        {d && d.error && <div className="pa-banner err">{d.error}</div>}
        {d && d !== 'loading' && !d.error && (
          <table className="pa-table sa-days">
            <thead><tr><th>{t('xSctActual.dDate')}</th><th>{t('xSctActual.dVendor')}</th><th>{t('xSctActual.dLine')}</th><th>{t('xSctActual.dPo')}</th>
              <th className="num">{t('xSctActual.dPieces')}</th><th className="num">{t('xSctActual.dWorkers')}</th>
              <th className="num">{t('xSctActual.dHours')}</th><th className="num">{t('xSctActual.dManHPc')}</th><th className="num">{t('xSctActual.dEff')}</th></tr></thead>
            <tbody>
              {d.days.map((x, i) => {
                const a = (x.workers * x.hours) / x.pieces;
                const e = r.ct / a;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    <td>{ddmm(x.day)}</td><td>{x.vendor}</td><td>{x.line || '—'}</td><td className="mono">{x.po || '—'}</td>
                    <td className="num">{n0(x.pieces)}</td><td className="num">{x.workers}</td><td className="num">{x.hours}</td>
                    <td className="num">{n2(a)}</td>
                    <td className="num"><span className={e > data.band.max ? 'sa-eff over' : e < data.band.min ? 'sa-eff under' : 'sa-eff'}>{pct(e)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const band = data?.band;
  return (
    <div className="sa">
      <div className="sa-intro">
        {t('xSctActual.intro')}
        {band && <>{' '}{fillParts(t('xSctActual.introBand', { range: marker('range'), minDays: band.minDays, tag: marker('tag') }),
          { range: <b>{pct(band.min)} – {pct(band.max)}</b>, tag: <AiTag /> })}</>}
      </div>

      <SaPeople t={t} base={base} canEdit={canAnswer} />

      <div className="pa-toolbar sa-tools">
        <div className="sa-presets" role="group" aria-label={t('xSctActual.period')}>
          {PRESETS.map((p) => (
            <button key={p.key} type="button" className={`sa-pill${preset === p.key ? ' on' : ''}`} aria-pressed={preset === p.key}
              onClick={() => pickPreset(p.key)}>{presetLabel[p.key]}</button>
          ))}
        </div>
        <input type="date" value={from} max={to} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} aria-label={t('xSctActual.from')} />
        <span className="pa-muted">–</span>
        <input type="date" value={to} min={from} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} aria-label={t('xSctActual.to')} />
        <select value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label={t('xSctActual.vendor')}>
          <option value="">{t('xSctActual.allVendors')}</option><option value="MS">MS</option><option value="TS">TS</option>
        </select>
        <button type="button" className="pa-btn" onClick={load} disabled={busy} title={t('xSctActual.reload')}>
          {busy ? <Loader2 size={14} className="pa-spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      <div className="pa-toolbar sa-tools">
        <input className="sa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xSctActual.search')} />
        <div className="sa-chips" role="group" aria-label={t('xSctActual.status')}>
          {[['reply', statusLabel.reply], ['handled', statusLabel.handled], ['ok', statusLabel.ok], ['thin', statusLabel.thin], ['all', t('xSctActual.stAll')]].map(([k, l]) => (
            <button key={k} type="button" className={`sa-pill ${k}${chip === k ? ' on' : ''}`} aria-pressed={chip === k}
              onClick={() => setChip(k)}>{l} <b>{counts[k] || 0}</b></button>
          ))}
        </div>
        {(q || tools.active) && <button type="button" className="pa-btn" onClick={() => { setQ(''); tools.clear(); }}>{t('xSctActual.clear')}</button>}
        <span className="pa-muted">{t('xSctActual.nOfM', { n: tools.shown.length, total: rows.length })}</span>
      </div>

      {err && <div className="pa-banner err">{err}</div>}
      {data && (data.skipped.noTime > 0 || !data.aiOn) && (
        <div className="sa-note-line pa-muted">
          {data.skipped.noTime > 0 && <>{t('xSctActual.skippedNoTime', { n: data.skipped.noTime })}{' '}</>}
          {!data.aiOn && <>{t('xSctActual.aiOff')}</>}
        </div>
      )}

      {!data ? (
        <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('xSctActual.measuring')}</div>
      ) : (
        <div className="pa-section sa-list">
          <div className="pa-table-wrap sa-scroll">
            <table className="pa-table sa-table">
              <thead>
                <tr>
                  <th className="sa-foldcol" aria-label={t('xSctActual.openCol')} />
                  {columns.map((c) => <SortTh key={c.key} label={c.label} {...tools.thProps(c.key)} />)}
                </tr>
              </thead>
              <tbody>
                {!tools.shown.length && <tr><td colSpan={columns.length + 1} className="pa-muted">{t('xSctActual.nothingHere')}</td></tr>}
                {tools.shown.map((r) => {
                  const isOpen = open === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr data-sa={r.id} className={`sa-row ${r.st}${isOpen ? ' open' : ''}`} onClick={() => toggle(r)}>
                        <td className="sa-foldcol">
                          <button type="button" className="sa-fold" aria-expanded={isOpen} aria-label={isOpen ? t('xSctActual.close') : t('xSctActual.open')}
                            onClick={(e) => { e.stopPropagation(); toggle(r); }}>
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        {columns.map((c) => (
                          <td key={c.key} className={c.td || ''}>{c.cell ? c.cell(r) : (c.get(r) === '' || c.get(r) == null ? '—' : c.get(r))}</td>
                        ))}
                      </tr>
                      {isOpen && (
                        <tr className="sa-detail-row"><td colSpan={columns.length + 1} onClick={(e) => e.stopPropagation()}>{detailView(r)}</td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
